import {
    ABSENTEE_BID_SUCCESS,
    LOAD_BIDDER_DATA_SUCCESS,
    LOAD_UPCOMING_ITEMS_FAIL,
    LOAD_UPCOMING_ITEMS_REQUEST,
    LOAD_UPCOMING_ITEMS_SUCCESS,
    LOG_OUT_BIDDER,
    LOGIN_FAIL,
    LOGIN_SUCCESS,
    SAVE_ITEM_SUCCESS,
    UNSAVE_ITEM_SUCCESS,
} from './actions';
import { getAuthToken, getBidderId } from './user';
import { createSelector } from 'reselect';
import { fetchUpcomingItemData } from '@/redux/api/bidder';
import { getDeployment } from './config';
import { GlobalState } from '../rootReducer';
import ms from 'ms';
import { PASSWORD_RESET_SUCCESS } from './passwordReset';

const REDUX_STORE_TIME = ms('30m');

// reducer
const DEFAULT_STATE = {
    error: false,
    errorMessage: null,
    itemIds: [],
    loaded: null,
    submitted: false,
    success: false,
};

export type State = typeof DEFAULT_STATE & {
    errorMessage: string | null | undefined;
    loaded: any | null | undefined;
};

export default function reducer(state: State = DEFAULT_STATE, action: any = {}) {
    switch (action.type) {
        case LOAD_BIDDER_DATA_SUCCESS:
        case LOGIN_SUCCESS:
        case PASSWORD_RESET_SUCCESS:
            const items = new Set([...state.itemIds]);

            if (action.payload.biddingInfos) {
                action.payload.biddingInfos.forEach((item) => {
                    items.add(item.itemId);
                });
            }

            return {
                ...state,
                // @ts-ignore
                itemIds: [...items],
            };
        case LOAD_UPCOMING_ITEMS_REQUEST:
            return {
                ...state,
                error: false,
                errorMessage: null,
                submitted: true,
                success: false,
            };
        case LOAD_UPCOMING_ITEMS_SUCCESS:
            return {
                ...state,
                // @ts-ignore
                itemIds: [...new Set([...state.itemIds, ...action.payload])],
                loaded: action.meta.actionTime,
                submitted: false,
                success: true,
            };
        case LOAD_UPCOMING_ITEMS_FAIL:
            return {
                ...state,
                error: true,
                errorMessage: action.payload,
                submitted: false,
                success: false,
            };
        case SAVE_ITEM_SUCCESS:
            if (action?.meta?.analytics?.eventPayload?.properties?.biddingInfo?.isAvailable) {
                return {
                    ...state,
                    // @ts-ignore
                    itemIds: [...new Set([...state.itemIds, action.payload])],
                };
            }

            return {
                ...state,
            };
        case UNSAVE_ITEM_SUCCESS:
            const itemIds = new Set(state.itemIds);
            itemIds.delete(action.payload);
            return {
                ...state,
                // @ts-ignore
                itemIds: [...itemIds],
            };
        case ABSENTEE_BID_SUCCESS:
            return {
                ...state,
                // @ts-ignore
                itemIds: [...new Set([...state.itemIds, action.meta.itemId])],
            };
        case LOG_OUT_BIDDER:
        case LOGIN_FAIL:
            return DEFAULT_STATE;
        default:
            return state;
    }
}

const stateSelector = (state: GlobalState) => state.upcomingItems;
export const getHasLoadedUpcomingIds = createSelector(stateSelector, (state) => Boolean(state.loaded));

export const getUpcomingItemIds = createSelector(stateSelector, (state) => state.itemIds || []);

const shouldFetchUpcomingItems = createSelector(stateSelector, (state) => {
    const { loaded, submitted } = state;
    if (submitted) {
        return false;
    }

    const time = Date.now();
    const diff = time - loaded;

    return diff >= REDUX_STORE_TIME;
});

const fetchUpcomingItems = (authToken: string, bidderId: number) => async (dispatch: Function, getState: Function) => {
    const state = getState();
    const deployment = getDeployment(state);

    try {
        dispatch({
            type: LOAD_UPCOMING_ITEMS_REQUEST,
        });
        const response = await fetchUpcomingItemData({ authToken, bidderId, deployment });
        return dispatch({
            meta: { actionTime: Date.now() },
            payload: response.data,
            type: LOAD_UPCOMING_ITEMS_SUCCESS,
        });
    } catch (error) {
        dispatch({
            error: true,
            payload: error,
            type: LOAD_UPCOMING_ITEMS_FAIL,
        });
    }
};

export const fetchUpcomingItemsIfNeeded = () => async (dispatch: Function, getState: Function) => {
    const state = getState();
    const bidderId = getBidderId(state);
    const authToken = getAuthToken(state);

    if (bidderId > 0 && Boolean(authToken) && shouldFetchUpcomingItems(state)) {
        return dispatch(fetchUpcomingItems(authToken, bidderId));
    }
    return Promise.resolve();
};
